import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { AdminLayout } from '../../components/admin/AdminLayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { TranslateButton } from '../../components/admin/TranslateButton';
import RichTextEditor from '../../components/admin/RichTextEditor';
import { 
  Save, 
  Loader2,
  Flag,
  Plus,
  Trash2,
  ChevronDown,
  ChevronUp,
  Info,
  RefreshCw,
  Image,
  Edit,
  MapPin
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const languages = [
  { code: 'ca', name: 'Català', flag: '🇦🇩' },
  { code: 'es', name: 'Español', flag: '🇪🇸' },
  { code: 'fr', name: 'Français', flag: '🇫🇷' },
  { code: 'en', name: 'English', flag: '🇬🇧' },
];

export const AdminHoles = () => {
  const navigate = useNavigate();
  const { isAdmin, authFetch } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [initializing, setInitializing] = useState(false);
  const [holes, setHoles] = useState([]);
  const [courseStats, setCourseStats] = useState(null);
  const [expandedHoles, setExpandedHoles] = useState({});
  const [activeLang, setActiveLang] = useState('ca');
  const [editingHole, setEditingHole] = useState(null);

  useEffect(() => {
    fetchHoles();
    fetchCourseStats();
  }, []);

  const fetchHoles = async () => {
    setLoading(true);
    try {
      const response = await authFetch(`${API_URL}/api/holes?active_only=false`, {  });
      if (response.ok) {
        const data = await response.json();
        setHoles(data);
        // Expand all by default
        const expanded = {};
        data.forEach(h => { expanded[h.number] = false; });
        setExpandedHoles(expanded);
      }
    } catch (error) {
      console.error('Failed to fetch holes:', error);
      toast.error('Error carregant forats');
    } finally {
      setLoading(false);
    }
  };

  const fetchCourseStats = async () => {
    try {
      const response = await authFetch(`${API_URL}/api/course-stats`, {  });
      if (response.ok) {
        const data = await response.json();
        setCourseStats(data);
      }
    } catch (error) {
      console.error('Failed to fetch course stats:', error);
    }
  };

  const handleInitHoles = async () => {
    setInitializing(true);
    try {
      const response = await authFetch(`${API_URL}/api/admin/holes/init`, {
        method: 'POST',
        
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success(data.message);
        fetchHoles();
        fetchCourseStats();
      } else {
        const data = await response.json();
        toast.info(data.message || 'Els forats ja existeixen');
      }
    } catch (error) {
      toast.error('Error inicialitzant forats');
    } finally {
      setInitializing(false);
    }
  };

  const handleUpdateHole = async (holeNumber, updates) => {
    setSaving(true);
    try {
      const response = await authFetch(`${API_URL}/api/admin/holes/${holeNumber}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        
        body: JSON.stringify(updates)
      });

      if (response.ok) {
        toast.success(`Forat ${holeNumber} actualitzat`);
        fetchHoles();
        fetchCourseStats();
      } else {
        const data = await response.json();
        toast.error(data.detail || 'Error actualitzant forat');
      }
    } catch (error) {
      toast.error('Error actualitzant forat');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteHole = async (holeNumber) => {
    if (!window.confirm(`Segur que vols eliminar el forat ${holeNumber}?`)) return;
    
    try {
      const response = await authFetch(`${API_URL}/api/admin/holes/${holeNumber}`, {
        method: 'DELETE',
        
      });

      if (response.ok) {
        toast.success(`Forat ${holeNumber} eliminat`);
        fetchHoles();
        fetchCourseStats();
      } else {
        toast.error('Error eliminant forat');
      }
    } catch (error) {
      toast.error('Error eliminant forat');
    }
  };

  const toggleHole = (holeNumber) => {
    setExpandedHoles(prev => ({ ...prev, [holeNumber]: !prev[holeNumber] }));
  };

  const updateHoleField = (holeNumber, field, value) => {
    setHoles(prev => prev.map(h => 
      h.number === holeNumber ? { ...h, [field]: value } : h
    ));
  };

  const updateHoleMultiLangField = (holeNumber, field, value) => {
    setHoles(prev => prev.map(h => 
      h.number === holeNumber ? { 
        ...h, 
        [field]: { ...h[field], [activeLang]: value }
      } : h
    ));
  };

  // Handle translation for a multi-lang field
  const handleTranslation = (holeNumber, field, translations, translatedLanguages) => {
    if (!translations) return;
    setHoles(prev => prev.map(h => {
      if (h.number === holeNumber) {
        const currentField = h[field] || {};
        return {
          ...h,
          [field]: {
            ...currentField,
            [activeLang]: currentField[activeLang] || '',
            ...translations
          }
        };
      }
      return h;
    }));
    // Mark translated languages for visual feedback
    setTranslatedFields(prev => ({
      ...prev,
      [`${holeNumber}_${field}`]: translatedLanguages || []
    }));
  };

  // Track which fields have been translated (for visual feedback)
  const [translatedFields, setTranslatedFields] = useState({});

  // Clear translation markers after save
  const clearTranslationMarkers = () => {
    setTranslatedFields({});
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-[#1A4D2E]" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="admin-holes">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-display font-semibold text-[#1A4D2E] flex items-center gap-2">
              <Flag className="w-6 h-6" />
              Gestió de Forats
            </h1>
            <p className="text-[#666666] mt-1">
              Configura els 18 forats del camp de golf
            </p>
          </div>
          
          {isAdmin && (
            <Button
              onClick={handleInitHoles}
              disabled={initializing}
              variant="outline"
              className="gap-2"
            >
              {initializing ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <RefreshCw className="w-4 h-4" />
              )}
              Inicialitzar forats per defecte
            </Button>
          )}
        </div>

        {/* Course Stats Summary */}
        {courseStats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-white border border-[#E5E5E5] rounded-lg p-4 text-center">
              <p className="text-3xl font-display font-bold text-[#1A4D2E]">{courseStats.total_holes}</p>
              <p className="text-sm text-[#666666]">Forats</p>
            </div>
            <div className="bg-white border border-[#E5E5E5] rounded-lg p-4 text-center">
              <p className="text-3xl font-display font-bold text-[#1A4D2E]">{courseStats.total_par}</p>
              <p className="text-sm text-[#666666]">Par Total</p>
            </div>
            <div className="bg-white border border-[#E5E5E5] rounded-lg p-4 text-center">
              <p className="text-3xl font-display font-bold text-[#1A4D2E]">{courseStats.total_distance_meters}</p>
              <p className="text-sm text-[#666666]">Metres</p>
            </div>
            <div className="bg-white border border-[#E5E5E5] rounded-lg p-4 text-center">
              <p className="text-3xl font-display font-bold text-[#1A4D2E]">{courseStats.total_distance_yards}</p>
              <p className="text-sm text-[#666666]">Iardes</p>
            </div>
          </div>
        )}

        {/* Info banner about images */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-blue-800">
              <p className="font-semibold mb-1">Format d'imatges recomanat:</p>
              <ul className="list-disc list-inside space-y-0.5">
                <li><strong>Imatge del forat:</strong> 800×600 px, format JPG/WebP, màx. 200KB</li>
                <li><strong>Layout/Mapa:</strong> 600×400 px, format PNG amb fons transparent</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Language selector */}
        <div className="flex gap-2 bg-[#F5F5F5] p-1 rounded-lg w-fit">
          {languages.map((l) => {
            // Check if this language has pending translations
            const hasPendingTranslations = Object.values(translatedFields).some(
              langs => langs && langs.includes(l.code)
            );
            
            return (
              <button
                key={l.code}
                onClick={() => setActiveLang(l.code)}
                className={`px-3 py-1.5 rounded text-sm font-medium transition-colors relative ${
                  l.code === activeLang 
                    ? 'bg-white text-[#1A4D2E] shadow-sm' 
                    : 'text-[#666666] hover:text-[#333333]'
                } ${hasPendingTranslations ? 'ring-2 ring-amber-400 ring-offset-1' : ''}`}
              >
                {l.flag} {l.code.toUpperCase()}
                {hasPendingTranslations && (
                  <span className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-amber-400 rounded-full animate-pulse" 
                        title="Traduccions pendents de guardar" />
                )}
              </button>
            );
          })}
        </div>
        
        {/* Pending translations indicator */}
        {Object.keys(translatedFields).length > 0 && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 flex items-center gap-3">
            <div className="w-2 h-2 bg-amber-400 rounded-full animate-pulse" />
            <p className="text-sm text-amber-800">
              Hi ha traduccions pendents de guardar. Revisa cada idioma i clica "Guardar" per aplicar els canvis.
            </p>
          </div>
        )}

        {/* Holes list */}
        {holes.length === 0 ? (
          <div className="bg-[#F9F9F7] border border-dashed border-[#E5E5E5] rounded-lg p-12 text-center">
            <Flag className="w-12 h-12 text-[#CCCCCC] mx-auto mb-4" />
            <p className="text-[#666666] mb-4">No hi ha forats configurats</p>
            <Button onClick={handleInitHoles} variant="outline" className="gap-2">
              <RefreshCw className="w-4 h-4" />
              Inicialitzar els 18 forats
            </Button>
          </div>
        ) : (
          <div className="space-y-3">
            {holes.map((hole) => (
              <div 
                key={hole.number}
                className="bg-white border border-[#E5E5E5] rounded-lg overflow-hidden"
                data-testid={`hole-${hole.number}`}
              >
                {/* Hole header */}
                <div 
                  className="flex items-center justify-between px-4 py-3 bg-[#F9F9F7] cursor-pointer hover:bg-[#F5F5F5]"
                  onClick={() => toggleHole(hole.number)}
                >
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-full bg-[#1A4D2E] text-white flex items-center justify-center font-bold">
                      {hole.number}
                    </div>
                    <div>
                      <span className="font-semibold text-[#333333]">
                        Forat {hole.number}
                      </span>
                      <div className="flex gap-3 text-sm text-[#666666]">
                        <span>Par {hole.par}</span>
                        <span>{hole.distance_meters}m</span>
                        <span>Hcp {hole.handicap}</span>
                      </div>
                    </div>
                    {hole.image && (
                      <img src={hole.image} alt="" className="w-16 h-12 object-cover rounded ml-4" />
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    {isAdmin && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => { e.stopPropagation(); handleDeleteHole(hole.number); }}
                        className="text-red-500 hover:text-red-600 hover:bg-red-50"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                    {expandedHoles[hole.number] ? (
                      <ChevronUp className="w-5 h-5 text-[#888888]" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-[#888888]" />
                    )}
                  </div>
                </div>

                {/* Hole content */}
                {expandedHoles[hole.number] && (
                  <div className="p-6 border-t border-[#E5E5E5] space-y-6">
                    {/* Basic info - Admin only for par and handicap */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div className="space-y-2">
                        <Label>Par {!isAdmin && <span className="text-xs text-[#888]">(només admin)</span>}</Label>
                        <Input
                          type="number"
                          min={3}
                          max={5}
                          value={hole.par}
                          onChange={(e) => updateHoleField(hole.number, 'par', parseInt(e.target.value))}
                          disabled={!isAdmin}
                          data-testid={`hole-${hole.number}-par`}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Distància (metres)</Label>
                        <Input
                          type="number"
                          min={50}
                          max={600}
                          value={hole.distance_meters}
                          onChange={(e) => updateHoleField(hole.number, 'distance_meters', parseInt(e.target.value))}
                          data-testid={`hole-${hole.number}-distance`}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Distància (iardes)</Label>
                        <Input
                          type="number"
                          value={hole.distance_yards || Math.round(hole.distance_meters * 1.09361)}
                          disabled
                          className="bg-gray-50"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Handicap {!isAdmin && <span className="text-xs text-[#888]">(només admin)</span>}</Label>
                        <Input
                          type="number"
                          min={1}
                          max={18}
                          value={hole.handicap}
                          onChange={(e) => updateHoleField(hole.number, 'handicap', parseInt(e.target.value))}
                          disabled={!isAdmin}
                        />
                      </div>
                    </div>

                    {/* Images */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label className="flex items-center gap-2">
                          <Image className="w-4 h-4" />
                          Imatge del forat (800×600 px)
                        </Label>
                        <Input
                          value={hole.image || ''}
                          onChange={(e) => updateHoleField(hole.number, 'image', e.target.value)}
                          placeholder="https://..."
                        />
                        {hole.image && (
                          <img src={hole.image} alt={`Forat ${hole.number}`} className="mt-2 max-h-24 rounded border" />
                        )}
                      </div>
                      <div className="space-y-2">
                        <Label className="flex items-center gap-2">
                          <MapPin className="w-4 h-4" />
                          Layout/Mapa del forat (600×400 px)
                        </Label>
                        <Input
                          value={hole.layout_image || ''}
                          onChange={(e) => updateHoleField(hole.number, 'layout_image', e.target.value)}
                          placeholder="https://..."
                        />
                        {hole.layout_image && (
                          <img src={hole.layout_image} alt={`Layout forat ${hole.number}`} className="mt-2 max-h-24 rounded border" />
                        )}
                      </div>
                    </div>

                    {/* Description - Multi-language */}
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <Label>Descripció ({activeLang.toUpperCase()})</Label>
                        <TranslateButton
                          text={hole.description?.[activeLang]}
                          sourceLanguage={activeLang}
                          onTranslated={(translations, langs) => handleTranslation(hole.number, 'description', translations, langs)}
                          onLanguageSwitch={(lang) => setActiveLang(lang)}
                        />
                      </div>
                      <RichTextEditor
                        value={hole.description?.[activeLang] || ''}
                        onChange={(value) => updateHoleMultiLangField(hole.number, 'description', value)}
                        placeholder="Descripció del forat..."
                        minHeight={100}
                      />
                    </div>

                    {/* Tips - Multi-language */}
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <Label>Consells de joc ({activeLang.toUpperCase()})</Label>
                        <TranslateButton
                          text={hole.tips?.[activeLang]}
                          sourceLanguage={activeLang}
                          onTranslated={(translations, langs) => handleTranslation(hole.number, 'tips', translations, langs)}
                          onLanguageSwitch={(lang) => setActiveLang(lang)}
                        />
                      </div>
                      <RichTextEditor
                        value={hole.tips?.[activeLang] || ''}
                        onChange={(value) => updateHoleMultiLangField(hole.number, 'tips', value)}
                        placeholder="Consells per jugar aquest forat..."
                        minHeight={80}
                      />
                    </div>

                    {/* Save button */}
                    <div className="flex justify-end pt-2">
                      <Button
                        onClick={() => handleUpdateHole(hole.number, {
                          par: hole.par,
                          distance_meters: hole.distance_meters,
                          handicap: hole.handicap,
                          image: hole.image,
                          layout_image: hole.layout_image,
                          description: hole.description,
                          tips: hole.tips
                        })}
                        disabled={saving}
                        className="bg-[#1A4D2E] hover:bg-[#153d25] text-white gap-2"
                        data-testid={`save-hole-${hole.number}`}
                      >
                        {saving ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Save className="w-4 h-4" />
                        )}
                        Guardar Forat {hole.number}
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminHoles;
